import React, { useState } from 'react';
import axios from 'axios';
import { Home, CheckCircle2, XCircle, Clock, Edit2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { API, resolveImg, getToken } from './shared';
import ListingDetailPanel from './ListingDetailPanel';

const PendingListingsTab = ({ pendingListings, onRefresh, onEditListing }) => {
  const [selectedPending, setSelectedPending] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [expandedListing, setExpandedListing] = useState(null);

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/approve`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      onRefresh();
    } catch (error) { console.error('Error approving listing:', error); }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/reject`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      onRefresh();
    } catch (error) { console.error('Error rejecting listing:', error); }
  };

  const handleBulkAction = async (action) => {
    if (selectedPending.size === 0) return;
    if (action === 'delete' && !window.confirm(`Are you sure you want to permanently delete ${selectedPending.size} listing(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/admin/listings/bulk-${action}`, { listing_ids: Array.from(selectedPending) }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSelectedPending(new Set());
      onRefresh();
    } catch (error) { console.error(`Error bulk ${action}:`, error); }
    finally { setBulkLoading(false); }
  };

  const toggleSelect = (id) => {
    setSelectedPending(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <h2 className="text-2xl sm:text-3xl font-semibold">Pending Approval</h2>
        {selectedPending.size > 0 && (
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-full px-4 py-2" data-testid="pending-bulk-toolbar">
            <span className="text-sm font-medium text-slate-700">{selectedPending.size} selected</span>
            <button onClick={() => handleBulkAction('approve')} disabled={bulkLoading} className="h-9 px-5 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-all flex items-center gap-1.5 disabled:opacity-50" data-testid="bulk-approve-pending">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve All
            </button>
            <button onClick={() => handleBulkAction('reject')} disabled={bulkLoading} className="h-9 px-5 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-all flex items-center gap-1.5 disabled:opacity-50" data-testid="bulk-reject-pending">
              <XCircle className="w-3.5 h-3.5" /> Reject All
            </button>
            <button onClick={() => handleBulkAction('delete')} disabled={bulkLoading} className="h-9 px-5 rounded-full bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 transition-all flex items-center gap-1.5 disabled:opacity-50" data-testid="bulk-delete-pending">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}
      </div>
      {pendingListings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No pending listings</p>
        </div>
      ) : (
        <>
          {pendingListings.length > 1 && (
            <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm text-slate-600">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                checked={selectedPending.size === pendingListings.length}
                onChange={() => {
                  if (selectedPending.size === pendingListings.length) setSelectedPending(new Set());
                  else setSelectedPending(new Set(pendingListings.map(l => l.id)));
                }}
                data-testid="select-all-pending"
              />
              Select all
            </label>
          )}
          <div className="space-y-4" data-testid="pending-listings">
            {pendingListings.map((listing) => (
              <div key={listing.id} className={`bg-white rounded-2xl border p-6 transition-all ${selectedPending.has(listing.id) ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200'}`} data-testid={`pending-listing-${listing.id}`}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-start pt-1">
                    <input type="checkbox" className="w-4 h-4 rounded border-slate-300 accent-blue-600 cursor-pointer" checked={selectedPending.has(listing.id)} onChange={() => toggleSelect(listing.id)} data-testid={`checkbox-pending-${listing.id}`} />
                  </div>
                  <div className="flex-shrink-0 w-48 h-32 bg-slate-200 rounded-lg overflow-hidden">
                    {listing.images && listing.images.length > 0 ? (
                      <img src={resolveImg(listing.images[0])} alt={listing.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Home className="w-12 h-12 text-slate-400" /></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{listing.title}</h3>
                    <p className="text-slate-600 text-sm mb-3 line-clamp-2">{listing.description}</p>
                    <div className="flex gap-4 text-sm text-slate-500 mb-4">
                      <span className="capitalize">{listing.property_type}</span>
                      <span>•</span>
                      <span>{listing.locality}, {listing.city}</span>
                      <span>•</span>
                      <span className="capitalize">{listing.seller_type}</span>
                      {(listing.size || listing.size_sqft) && <><span>•</span><span>{listing.size || `${listing.size_sqft} sq.ft`}</span></>}
                      {listing.land_size && <><span>•</span><span>Land: {listing.land_size}</span></>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => handleApprove(listing.id)} className="h-10 px-6 rounded-full bg-green-600 text-white font-medium hover:bg-green-700 transition-all flex items-center gap-2" data-testid={`approve-${listing.id}`}>
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button onClick={() => handleReject(listing.id)} className="h-10 px-6 rounded-full bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center gap-2" data-testid={`reject-${listing.id}`}>
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                      <button onClick={() => onEditListing(listing)} className="h-10 px-5 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2" data-testid={`edit-pending-${listing.id}`}>
                        <Edit2 className="w-4 h-4" /> Edit
                      </button>
                      <button onClick={() => setExpandedListing(expandedListing === listing.id ? null : listing.id)} className="h-10 px-5 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2" data-testid={`view-details-${listing.id}`}>
                        {expandedListing === listing.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {expandedListing === listing.id ? 'Hide' : 'Details'}
                      </button>
                    </div>
                  </div>
                </div>
                {expandedListing === listing.id && <ListingDetailPanel listing={listing} />}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PendingListingsTab;
